// src/components/routes/index.js

import {
  getContactsWithEmails,
  deleteContact,
  getCustomAttributes,
} from 'actions/contacts'
import { getAllGroups, deleteGroup } from 'actions/contacts/groups'
import { listTasks, listTaskTypes, deleteTask } from 'actions/tasks'
import { listTemplates, deleteTemplate } from 'actions/template'
import { list, deleteEntity } from 'actions/data'
import taskTypeIDToNameMap from 'utils/taskTypeIDToNameMap'
import shortener_urls from 'utils/ShortenerUrl'
import CampaignIcon from 'components/theme/CampaignIcon'
import ContactsIcon from '@material-ui/icons/Contacts'
import GroupIcon from '@material-ui/icons/Group'
import MailOutlineIcon from '@material-ui/icons/MailOutline'
import DoneAllIcon from '@material-ui/icons/DoneAll'
import Person from '@material-ui/icons/Person'
import Link from '@material-ui/icons/Link'
import AddOrEditURL from 'components/AddOrEditURL'
import { FileTable } from 'mdi-material-ui'
import React from 'react'
import { reduce } from 'lodash'
import { DATA_API_URL } from 'utils/AllURLs'
import DudeReadingV2Icon from 'components/theme/DudeReadingV2Icon'
import { displayName } from 'components/ListView/contact/ListItemNormal'
// import { /* openSnackbar, */ forceListUpdate } from 'actions/view'

export const contact = {
  helperText:
    'Contacts are the people you market to. You can import your contacts and their data in one place so you can begin managing contact groups for your distribution lists and automating your work-flow.',
  guideUrl: 'https://vastreach.com/contacts',
  path: '/contacts',
  endpoint: 'contact',
  listState: state => {
    const modifiedListState = Object.keys(
      state.contacts.contactsWithEmailsByID,
    ).reduce((acc, el) => {
      const contact = state.contacts.contactsWithEmailsByID?.[el]
      acc[el] = contact

      if (contact.first_name || contact.middle_name || contact.last_name) {
        acc[el].full_name = `${
          contact.first_name ? `${contact.first_name} ` : ''
        }${contact.middle_name ? `${contact.middle_name} ` : ''}${
          contact.last_name || ''
        }`

        if (contact.first_name || contact.last_name) {
          acc[el].first_and_last_name = `${
            contact.first_name ? `${contact.first_name} ` : ''
          }${contact.last_name || ''}`
        }
      }
      return acc
    }, {})

    return modifiedListState
  },
  listFn: {
    getContactsWithEmails,
    getCustomAttributes,
    getAllGroups,
    listContactUserpins: () =>
      list(
        'userpin',
        null,
        `${DATA_API_URL}/userpin/username-entity?secondary_id=contact`,
      ),
  },
  bulkUpload: true,
  skeleton: false,
  keys: {
    sortable: [
      'when_created',
      'when_assigned',
      'when_last_inbound',
      'when_last_outbound',
      'first_name',
      'middle_name',
      'last_name',
      'location',
      'vastuser',
    ],
    nonSortable: [
      'email.address',
      'phones.number',
      'first_and_last_name',
      'full_name',
    ],
  },
  deleteFn: deleteContact,
  loadStatus: state => state.loadStatus.contacts,
  icon: <ContactsIcon />,
  displayName: (state, id) => {
    const contact = state.contacts.contactsWithEmailsByID?.[id]
    return displayName(contact) || 'Unknown Contact'
  },
}

export const task = {
  helperText:
    'Tasks allow you to automate your day-to-day. You can schedule a task or automate it by adding it a campaign funnel or calendar. Get started by clicking the create task button or click here to ',
  guideUrl: 'https://vastreach.com/tasks',
  path: '/tasks',
  endpoint: 'task',
  listState: state => state.tasks.tasksByID,
  listFn: {
    listTasks,
    listTaskTypes,
    listTemplates,
    listTaskUserpins: () =>
      list(
        'userpin',
        null,
        `${DATA_API_URL}/userpin/username-entity?secondary_id=task`,
      ),
  },
  calendarView: true,
  dataPanels: true,
  keys: {
    areFunctions: true,
    sortable: {
      when_created: task => task.when_created,
      scheduled: task => {
        const taskData =
          task[taskTypeIDToNameMap[task.task_type_id].payload_name]
        return taskData.when_to_send || taskData.when_to_trigger
      },
      opens: task =>
        task.send_email || task.send_sms
          ? task.email_stats && task.email_stats.total_opens
            ? task.email_stats.total_opens
            : task.sms_stats && task.sms_stats.total_opens
            ? task.sms_stats.total_opens
            : 0
          : false,
      sends: task =>
        task.send_email || task.send_sms
          ? task.email_stats && task.email_stats.total_sends
            ? task.email_stats.total_sends
            : task.sms_stats && task.sms_stats.total_sends
            ? task.sms_stats.total_sends
            : 0
          : false,
      clicks: task =>
        task.send_email
          ? task.email_stats && task.email_stats.total_clicks
            ? task.email_stats.total_clicks
            : 0
          : false,
      name: task => task.name.toLowerCase(),
    },
  },
  deleteFn: deleteTask,
  loadStatus: state => state.loadStatus.tasks,
  icon: <DoneAllIcon />,
  displayName: (state, id) => {
    const task = state.tasks.tasksByID?.[id]
    return task?.name || 'Unknown Task'
  },
}

export const group = {
  helperText:
    'Contact Groups help you organize your contacts for different marketing strategies and campaigns.',
  guideUrl: 'https://vastreach.com/groups',
  path: '/contactgroups',
  title: 'Contact Groups',
  endpoint: 'contactgroup',
  addOrEditPath: id => `/contactgroups/${id}`,
  shortLabel: 'group',
  listState: state => state.contacts.groupsByID,
  listFn: {
    getAllGroups,
    listGroupUserpins: () =>
      list(
        'userpin',
        null,
        `${DATA_API_URL}/userpin/username-entity?secondary_id=contactgroup`,
      ),
  },
  deleteFn: deleteGroup,
  keys: { sortable: ['contact_count', 'name'] },
  loadStatus: state => state.loadStatus.groups,
  icon: <GroupIcon />,
  displayName: (state, id) => {
    const group = state.contacts.groupsByID?.[id]
    return group?.name || 'Unknown Group'
  },
}

export const form = {
  path: '/forms',
  title: 'Forms',
  endpoint: 'form',
  addOrEditPath: (id, type) => `/forms/${type}/${id}`,
  customAddPath: '/forms/unknown/new',
  shortLabel: 'form',
  listState: state => ({
    ...state.signupForm.forms,
    ...reduce(
      state.data?.customform || {},
      (result, value, key) => {
        result[key] = { ...value, type: 'customform' }
        return result
      },
      {},
    ),
  }),
  listFn: {
    listCustomForms: () =>
      list('customform' /* , null, null, { type: 'customform' } */),
    listFormUserpins: () =>
      list(
        'userpin',
        null,
        `${DATA_API_URL}/userpin/username-entity?secondary_id=customform`,
      ),
  },
  dataPanels: true,
  deleteFn: id => deleteEntity(id, 'customform'),
  keys: { sortable: ['when_created', 'name', 'display_type'] },
  loadStatus: state => state.loadStatus.forms,
  icon: <FileTable />,
  displayName: (state, id) => {
    const form = state.data?.customform?.[id]
    return form?.name || 'Unknown Form'
  },
}

const recursiveListKnowledge =
  (returnedCursor = null, seenCursors = new Set()) =>
  dispatch => {
    if (returnedCursor) {
      // If we have seen this cursor before, end recursion to avoid an infinite loop
      if (seenCursors.has(returnedCursor)) {
        console.log('Repeated cursor detected, ending recursion.')
        return
      }

      // Add the current cursor to the set of seen cursors
      seenCursors.add(returnedCursor)

      return dispatch(
        list(
          'knowledge',
          null,
          `${DATA_API_URL}/knowledge${
            typeof returnedCursor === 'string' ? `?cur=${returnedCursor}` : ''
          }`,
        ),
      ).then(payload => {
        console.log('.then(payload => { ', payload)
        if (payload?.cursor) {
          return dispatch(recursiveListKnowledge(payload.cursor, seenCursors))
        } else {
          // Base case: No cursor present, end recursion
          console.log('No more cursor, ending recursion.')
          return
        }
      })
    }

    // Base case: Initial call without a cursor, end recursion
    console.log('Initial call without cursor, ending recursion.')
    return
  }

export const knowledge = {
  path: '/knowledge',
  title: 'Knowledge',
  endpoint: 'knowledge',
  addOrEditPath: (id, type) => `/knowledge/${id}`,
  icon: <DudeReadingV2Icon />,
  listFn: {
    // listKnowledge: () => list('knowledge', null, `${DATA_API_URL}/knowledge`),
    listKnowledge: () => recursiveListKnowledge(true),
    listKnowledgeUserpins: () =>
      list(
        'userpin',
        null,
        `${DATA_API_URL}/userpin/username-entity?secondary_id=knowledge`,
      ),
  },
  loadStatus: state => state.loadStatus.knowledge,
  keys: {
    sortable: ['title', 'created_date', 'updated_date'],
  },
  deleteFn: id =>
    deleteEntity(id, 'knowledge', `${DATA_API_URL}/knowledge/${id}`),
  helperText: `This your hub for storing and sharing team and customer knowledge. Our API uses OpenAI's NLP capabilities to generate responses to inbound messages. Experience seamless knowledge management and smarter communication.`,
}

export const campaign = {
  helperText:
    'Campaign’s automatically manage your tasks, or delegate them to your sales team, based on pre-determined goals. The Campaign Calendar also allows you to schedule your tasks to execute at a specific date and time.',
  guideUrl: 'https://vastreach.com/campaigns',
  path: '/campaigns',
  endpoint: 'campaign',
  keys: {
    sortable: ['status_', 'when_created', 'start_date', 'end_date', 'name'],
  },
  loadStatus: state => state.loadStatus.campaigns,
  dataPanels: true,
  icon: <CampaignIcon />,
  listState: state => {
    const modifiedListState = Object.keys(state.data?.campaign || {}).reduce(
      (acc, el) => {
        const campaign = state.data?.campaign?.[el]
        acc[el] = campaign

        const statusOrder = { L: 1, P: 2, W: 3, C: 4 }
        acc[el].status_ = statusOrder?.[campaign?.status] || 5

        return acc
      },
      {},
    )

    return modifiedListState
  },
}

export const urls = {
  helperText:
    'Short URLs allow you to take long urls, shorten them and track their clicks/visits ',
  guideUrl: 'https://vastreach.com/campaigns',
  path: '/urls',
  title: 'Short URLs',
  keys: { sortable: ['date_created', 'visits', 'long_url', 'url_code'] },
  endpoint: 'url',
  loadStatus: state => state.loadStatus.urls,
  addOrEditComponent: <AddOrEditURL />,
  defaultSortOrder: 'desc',
  dataPanels: false,
  listFn: {
    listUrls: () => list('url', 'url_code', shortener_urls.api_url + '/list'),
    listUrlUserpins: () =>
      list(
        'userpin',
        null,
        `${DATA_API_URL}/userpin/username-entity?secondary_id=url`,
      ),
  },
  icon: <Link />,
}

export const template = {
  helperText:
    'Templates are your saved email templates you have made. This allows you to use a template you made in the past for future marketing without having to redesign it.',
  guideUrl: 'https://vastreach.com/faq',
  path: '/templates',
  title: 'Templates',
  endpoint: 'template',
  addOrEditPath: (id, entitySubtype) => `/templates/${entitySubtype}/${id}`,
  customAddPath: '/templates/unknown/new',
  shortLabel: 'template',
  listState: state => ({
    ...state.email.templatesById,
    ...reduce(
      state.data?.['sms/template'] || {},
      (result, value, key) => {
        result[key] = { ...value, type: 'sms' }
        return result
      },
      {},
    ),
  }),
  listFn: {
    listTemplates,
    listSmsTemplates: () => list('sms/template'),
    listUsers: () => list('account/user', 'user_name'),
    listSmsTemplateUserpins: () =>
      list(
        'userpin',
        null,
        `${DATA_API_URL}/userpin/username-entity?secondary_id=${encodeURIComponent(
          'sms/template',
        )}`,
      ),
    listEmailTemplateUserpins: () =>
      list(
        'userpin',
        null,
        `${DATA_API_URL}/userpin/username-entity?secondary_id=${encodeURIComponent(
          'email/template',
        )}`,
      ),
  },
  // listFn: {
  // 	listSignupForm,
  // 	listCustomForms: () =>
  // 		list('customform' /* , null, null, { type: 'customform' } */),
  // },
  deleteFn: (id, entitySubtype) =>
    entitySubtype === 'sms'
      ? deleteEntity(id, 'sms/template')
      : deleteTemplate(id),
  keys: { sortable: ['when_created', 'when_modified', 'name'] },
  loadStatus: state => state.loadStatus.templatesById,
  icon: <MailOutlineIcon />,
}

export const user = {
  helperText:
    'Users are created by the account owner of a Vast Reach account to assist with their business needs.',
  guideUrl: 'https://vastreach.com/contacts',
  title: 'Users',
  endpoint: 'account/user',
  keys: {
    sortable: [
      'user_name',
      'first_name',
      'last_name',
      'email',
      'phone_number',
      'when_created',
      'when_modified',
    ],
  },
  loadStatus: state => state.loadStatus.users,
  icon: <Person />,
  deleteFn: (id, type) =>
    deleteEntity(
      id,
      'account/user',
      null,
      'none' /* , payload => {
      openSnackbar({
        type: 'success',
        message: `User ${payload?.username} deleted.${
          payload?.user_contact_count
            ? ` ${payload.user_contact_count} contacts unassigned.`
            : ''
        }`,
        detail: 'userDeleted',
      })
    } */,
    ),
}

// {
//   path: '/scheduledemailslist',
//   title: 'Scheduled Emails',
//   endpoint: 'email/schedule',
// },
// {
//   path: '/completedsendslist',
//   title: 'Completed Sends',
//   endpoint: 'email/completed',
// },
// { path: '/managePhonesPage' }
